import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { BarChart3, BookOpen, Calendar, Clock, ShieldCheck, Table2, Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useSolicitationInsights } from '../../../../hooks/useSolicitationInsights';
import { ComplianceMatrixContainer } from '../../ComplianceMatrix';
import { ActivityView } from '../../ComplianceMatrix/workspace/views/ActivityView';
import { EventsView } from '../../ComplianceMatrix/workspace/views/EventsView';
import { GatesView } from '../../ComplianceMatrix/workspace/views/GatesView';
import { OverviewView } from '../../ComplianceMatrix/workspace/views/OverviewView';
import { PeopleView } from '../../ComplianceMatrix/workspace/views/PeopleView';
import { VolumesView } from '../../ComplianceMatrix/workspace/views/VolumesView';
import { groupByLogicalKey } from '../../v2/amendment/groupByLogicalKey';
import { useWorkspace } from '../workspace/WorkspaceContext';
/**
 * Compliance Hub — multi-view tabbed workspace.
 *
 * Each tab is a role-focused view so users see only the facts they care
 * about instead of scrolling through 300+ facts on one page.
 *
 * Tabs:
 *   Overview  — health dashboard, fact counts, critical items
 *   Matrix    — requirements table (existing ComplianceMatrixContainer)
 *   Volumes   — volume structure + page limits + format rules + content/file reqs
 *   People    — key personnel + resume reqs + past performance + subcontracting
 *   Events    — deadlines + oral/ITD + Q&A + site visits
 *   Gates     — security + bonds + teaming + award + contract type + PoP
 */ export function ComplianceHubPage() {
    const [params, setParams] = useSearchParams();
    const { id } = useParams();
    const { data: insights } = useSolicitationInsights(id ?? '');
    const pipeline = insights?.pipeline;
    const counts = useMemo(()=>computeTabCounts(pipeline), [
        pipeline
    ]);
    const { workspace, workspaceId, showTab } = useWorkspace(id);
    if (!id) return null;
    const tabTriggerClass = 'relative rounded-none border-b-2 border-transparent px-3 py-2.5 text-[13px] font-medium text-slate-500 hover:text-slate-800 data-[state=active]:border-slate-900 data-[state=active]:bg-transparent data-[state=active]:text-slate-900 data-[state=active]:shadow-none';
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto flex h-full w-full max-w-7xl flex-col px-4 pt-4 sm:px-6 lg:px-8",
        children: [
            /*#__PURE__*/ _jsx("header", {
                className: "mb-3",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsx("h1", {
                            className: "text-lg font-semibold text-slate-900",
                            children: "Compliance Hub"
                        }),
                        counts.total > 0 && /*#__PURE__*/ _jsxs("span", {
                            className: "text-xs text-slate-500",
                            children: [
                                counts.total,
                                " insights"
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500",
                            children: [
                                workspace.label,
                                " view"
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(Tabs, {
                value: params.get('complianceTab') || workspace.defaultTab,
                onValueChange: (value)=>setParams((old)=>{
                        const next = new URLSearchParams(old);
                        next.set('complianceTab', value);
                        return next;
                    }, {
                        replace: true
                    }),
                className: "flex w-full flex-1 flex-col overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsxs(TabsList, {
                        className: "h-auto w-full justify-start gap-0 rounded-none border-b border-slate-200 bg-transparent p-0",
                        children: [
                            showTab('overview') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "overview",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(BarChart3, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Overview"
                                    ]
                                })
                            }),
                            showTab('matrix') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "matrix",
                                className: tabTriggerClass,
                                "data-testid": Testids.compliance.matrixTabTrigger,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Table2, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Matrix"
                                    ]
                                })
                            }),
                            showTab('volumes') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "volumes",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(BookOpen, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Volumes",
                                        counts.volumes > 0 && /*#__PURE__*/ _jsx(TabBadge, {
                                            count: counts.volumes
                                        })
                                    ]
                                })
                            }),
                            showTab('people') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "people",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Users, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "People",
                                        counts.people > 0 && /*#__PURE__*/ _jsx(TabBadge, {
                                            count: counts.people
                                        })
                                    ]
                                })
                            }),
                            showTab('events') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "events",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Calendar, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Events",
                                        counts.events > 0 && /*#__PURE__*/ _jsx(TabBadge, {
                                            count: counts.events
                                        })
                                    ]
                                })
                            }),
                            showTab('gates') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "gates",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(ShieldCheck, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Gates",
                                        counts.gates > 0 && /*#__PURE__*/ _jsx(TabBadge, {
                                            count: counts.gates
                                        })
                                    ]
                                })
                            }),
                            showTab('activity') && /*#__PURE__*/ _jsx(TabsTrigger, {
                                value: "activity",
                                className: tabTriggerClass,
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx(Clock, {
                                            className: "h-3.5 w-3.5",
                                            "aria-hidden": "true"
                                        }),
                                        "Activity"
                                    ]
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 overflow-auto pt-4",
                        children: [
                            showTab('overview') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "overview",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: pipeline ? /*#__PURE__*/ _jsx(OverviewView, {
                                    pipeline: pipeline,
                                    projectId: id
                                }) : /*#__PURE__*/ _jsx(EmptyState, {})
                            }),
                            showTab('matrix') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "matrix",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: /*#__PURE__*/ _jsx(ComplianceMatrixContainer, {
                                    projectId: id
                                })
                            }),
                            showTab('volumes') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "volumes",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: pipeline ? /*#__PURE__*/ _jsx(VolumesView, {
                                    pipeline: pipeline,
                                    projectId: id
                                }) : /*#__PURE__*/ _jsx(EmptyState, {})
                            }),
                            showTab('people') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "people",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: pipeline ? /*#__PURE__*/ _jsx(PeopleView, {
                                    pipeline: pipeline,
                                    projectId: id
                                }) : /*#__PURE__*/ _jsx(EmptyState, {})
                            }),
                            showTab('events') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "events",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: pipeline ? /*#__PURE__*/ _jsx(EventsView, {
                                    pipeline: pipeline,
                                    projectId: id
                                }) : /*#__PURE__*/ _jsx(EmptyState, {})
                            }),
                            showTab('gates') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "gates",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: pipeline ? /*#__PURE__*/ _jsx(GatesView, {
                                    pipeline: pipeline,
                                    projectId: id
                                }) : /*#__PURE__*/ _jsx(EmptyState, {})
                            }),
                            showTab('activity') && /*#__PURE__*/ _jsx(TabsContent, {
                                value: "activity",
                                forceMount: true,
                                className: "m-0 data-[state=inactive]:hidden",
                                children: /*#__PURE__*/ _jsx(ActivityView, {
                                    projectId: id
                                })
                            })
                        ]
                    })
                ]
            }, workspaceId)
        ]
    });
}
function TabBadge({ count }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-slate-100 px-1 text-[10px] font-semibold text-slate-600",
        children: count
    });
}
function EmptyState() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 py-20 text-center text-sm text-slate-500",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Nothing has been extracted here yet — that is not the same as there being no requirements."
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Check your source documents in",
                    ' ',
                    /*#__PURE__*/ _jsx(Link, {
                        className: "underline",
                        relative: "path",
                        to: "../sources/files",
                        children: "Sources"
                    }),
                    "."
                ]
            })
        ]
    });
}
/** Counts current-version facts per tab for the badge numbers. */ function computeTabCounts(pipeline) {
    if (!pipeline) return {
        total: 0,
        matrix: 0,
        volumes: 0,
        people: 0,
        events: 0,
        gates: 0
    };
    const count = (arr)=>{
        if (!arr || arr.length === 0) return 0;
        return groupByLogicalKey(arr).currentList.length;
    };
    const volumes = count(pipeline.volumes) + count(pipeline.pageLimits) + count(pipeline.formatRules) + count(pipeline.contentRequirements) + count(pipeline.fileRequirements);
    const people = count(pipeline.keyPersonnel) + count(pipeline.resumeRequirements) + count(pipeline.pastPerformance) + count(pipeline.cpars) + count(pipeline.subcontracting);
    const events = count(pipeline.deadlines) + count(pipeline.oralPresentations) + count(pipeline.qaProtocol) + count(pipeline.siteVisits) + count(pipeline.preProposalConfs);
    const gates = count(pipeline.securityClearances) + count(pipeline.bonds) + count(pipeline.teamingRestrictions) + count(pipeline.awardMechanisms) + count(pipeline.contractTypes) + count(pipeline.placesOfPerformance);
    const total = volumes + people + events + gates;
    return {
        total,
        matrix: 0,
        volumes,
        people,
        events,
        gates
    };
}
